import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Input,
  PlusIcon,
  SearchIcon,
  Tooltip,
  useDebouncedValue,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useHasOrganizationAction,
  useOrganizationMembersTotal,
} from '~publish/contexts/AccountContext'

import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsPageSkeleton } from '../shared/components/SettingsPageSkeleton'
import { EditMemberDialog } from './components/EditMemberDialog'
import { InviteMemberDialog } from './components/InviteMemberDialog'
import { NativeConfirmRemoveMemberDialog } from './components/NativeConfirmRemoveMemberDialog'
import { TeamMembersList } from './components/TeamMembersList'
import { MEMBER_SEARCH_DEBOUNCE_MS, MEMBER_SEARCH_THRESHOLD } from './constants'
import type { TeamMember } from './hooks'
import { RolesTeam } from './RolesTeam'
import { SecuritySection } from './SecuritySection'

import styles from './Team.module.css'

const Team = (): JSX.Element => {
  const { account, loading } = useAccount()
  const rolesEnabled = account?.currentOrganization?.isRolesEnabled ?? false

  // Wait for the account rather than flash the legacy page and swap.
  if (loading) {
    return <SettingsPageSkeleton />
  }

  if (rolesEnabled) {
    return <RolesTeam />
  } else {
    return <LegacyTeam />
  }
}

const LegacyTeam = (): JSX.Element => {
  const { account } = useAccount()
  const canViewTeamMembers = useHasOrganizationAction('viewTeamMembers')
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')
  const canEditOrganization = useHasOrganizationAction('edit')
  const memberCount = useOrganizationMembersTotal() ?? 0

  // Member search. Debounced so we don't fire a query on every keystroke; the
  // list filters server-side (see `useTeamMembers`). The list shows a
  // verification notice until the email is verified, so gate the search on the
  // same conditions to avoid rendering it above the forbidden/verification
  // states.
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    MEMBER_SEARCH_DEBOUNCE_MS,
  )
  const canSearchMembers =
    canViewTeamMembers &&
    !!account?.hasVerifiedEmail &&
    memberCount > MEMBER_SEARCH_THRESHOLD

  // Invite dialog state
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)

  // Remove dialog state
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null)

  const handleInvite = (): void => {
    setIsInviteDialogOpen(true)
  }

  const handleCloseInvite = (): void => {
    setIsInviteDialogOpen(false)
  }

  const handleEdit = (member: TeamMember): void => {
    setEditingMember(member)
    setIsEditDialogOpen(true)
  }

  const handleCloseEdit = (): void => {
    setIsEditDialogOpen(false)
    setEditingMember(null)
  }

  const handleRemove = (member: TeamMember): void => {
    setRemovingMember(member)
    setIsRemoveDialogOpen(true)
  }

  const handleCloseRemove = (): void => {
    setIsRemoveDialogOpen(false)
    setRemovingMember(null)
  }

  // `manageTeamMembers` needs a verified email. A user who can see the team
  // but hasn't verified yet gets a disabled button that says why.
  let inviteButton: JSX.Element | null = null
  if (canManageTeamMembers) {
    inviteButton = (
      <Button onClick={handleInvite}>
        <PlusIcon />
        Invite Member
      </Button>
    )
  } else if (canViewTeamMembers && !account?.hasVerifiedEmail) {
    inviteButton = (
      <Tooltip content="Verify your email address to invite team members">
        <Button disabled>
          <PlusIcon />
          Invite Member
        </Button>
      </Tooltip>
    )
  }

  return (
    <>
      <NewSettingsPageLayout
        title="Team Members"
        subtitle="Manage your team members and their access to channels."
        actions={inviteButton}
        inlineActionsOnMobile
      >
        {canEditOrganization && <SecuritySection />}
        {canSearchMembers && (
          <Input
            prefix={<SearchIcon />}
            size="large"
            placeholder="Search members"
            aria-label="Search team members"
            value={searchQuery}
            onChange={(e): void => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        )}
        <TeamMembersList
          search={canSearchMembers ? debouncedSearchQuery : ''}
          onEdit={handleEdit}
          onRemove={handleRemove}
        />
      </NewSettingsPageLayout>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        isOpen={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onClose={handleCloseInvite}
      />

      {/* Edit Member Dialog */}
      <EditMemberDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onClose={handleCloseEdit}
        member={editingMember}
      />

      {/* Remove Member Confirmation Dialog */}
      <NativeConfirmRemoveMemberDialog
        isOpen={isRemoveDialogOpen}
        onOpenChange={setIsRemoveDialogOpen}
        onClose={handleCloseRemove}
        member={removingMember}
      />
    </>
  )
}

export default Team
